import type { ApolloClient } from '@apollo/client';
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

import type { MeQuery } from '@/api/__generated__/graphql';
import { ME_QUERY } from '@/api/auth.api';
import type { TModuleCode } from '@/shared/routes';
import {
  EMPTY_ACCESSES,
  getAccesses
} from '@/shared/services/helpers/accesses';
import { toSessionModules } from '@/shared/services/helpers/modules';
import type { IAccesses, IEmployee } from '@/shared/types/session.types';

export type TSessionStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Сессия сотрудника: кто вошёл, какие разделы ему открыты и что разрешено.
 * Загружается один раз после входа и определяет доступные роуты и навигацию.
 */
interface ISessionState {
  status: TSessionStatus;
  employee: IEmployee | null;
  /** Открытые разделы в порядке навигации. */
  modules: TModuleCode[];
  accesses: IAccesses;
}

/** Ответ на загрузку сессии: сотрудник и коды его модулей, если бэкенд их прислал. */
export interface ISessionData {
  employee: IEmployee;
  moduleCodes?: string[] | null;
}

const initialState: ISessionState = {
  status: 'idle',
  employee: null,
  modules: [],
  accesses: EMPTY_ACCESSES
};

/** Сотрудник из ответа `me`: `uuid` → `id`, пустое отчество — без поля. */
export const toEmployee = ({
  uuid,
  firstName,
  lastName,
  middleName,
  email,
  position
}: MeQuery['me']): IEmployee => ({
  id: uuid,
  firstName,
  lastName,
  ...(middleName && { middleName }),
  email,
  position: position && {
    id: position.uuid,
    code: position.code,
    name: position.name
  }
});

/**
 * Коды модулей сотрудника — модули его должности: только активные, по `sortOrder`.
 * Без должности модулей нет.
 */
export const toModuleCodes = (position: MeQuery['me']['position']): string[] =>
  (position?.modules ?? [])
    .filter(({ isActive }) => isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ code }) => code);

/**
 * Загрузка сессии по токену — запрос `me` мимо кэша: данные одного сотрудника
 * не должны достаться другому после перезахода.
 */
export const loadSession = createAsyncThunk(
  'session/load',
  async (client: ApolloClient): Promise<ISessionData> => {
    const { data } = await client.query({
      query: ME_QUERY,
      fetchPolicy: 'network-only'
    });
    if (!data) throw new Error('Me query returned no data');

    return {
      employee: toEmployee(data.me),
      moduleCodes: toModuleCodes(data.me.position)
    };
  }
);

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /** Сброс при выходе и истечении сессии: данные одного сотрудника не достаются другому. */
    resetSession: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSession.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        loadSession.fulfilled,
        (state, action: PayloadAction<ISessionData>) => {
          const { employee, moduleCodes } = action.payload;
          state.status = 'ready';
          state.employee = employee;
          state.modules = toSessionModules(moduleCodes);
          // Разрешённых действий бэкенд пока не присылает.
          state.accesses = getAccesses(employee.position?.code ?? '', []);
        }
      )
      .addCase(loadSession.rejected, (state) => {
        state.status = 'error';
      });
  }
});

export const { resetSession } = sessionSlice.actions;
export default sessionSlice;
